import { PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE } from 'flare-chat-core/contracts/timeline/item-types';
import { buildCommittedArtifactItems } from './committedArtifacts.js';
import { selectMessagePresentation } from '../messagePresentation.js';
import { processIdentity, processProjectionSequence } from '../processProjection.js';
import { isArtifactCanvasPayloadAdmissible } from 'flare-chat-core/contracts/canvas/artifactCanvasAdmissibility';
import { messageIdentityKeyForExecutionProfile } from 'flare-chat-core/contracts/modes/execution-profile';
import { buildMessagePostGenerationArtifactItem } from './artifactDocument.js';

const firstTruthy = (...readers) => readers.reduce((result, read) => result || read(), undefined);
const everyTruthy = (...readers) => readers.reduce((result, read) => result && read(), true);

function resolveObject(value) {
  return (
    value
    && typeof value === 'object'
    && !Array.isArray(value)
  ) ? value : null;
}

function appendStreamingArtifactItems(items, streaming) {
  const roundKey = String(firstTruthy(() => (streaming.roundKey), () => (''))).trim();
  const canvasState = resolveObject(streaming.canvasState);
  if (everyTruthy(() => (canvasState), () => (isArtifactCanvasPayloadAdmissible(canvasState)))) {
    items.push({
      id: `canvas-state-${String(firstTruthy(() => (Object(canvasState).session_id), () => ('latest')))}`,
      type: 'canvas_state',
      payload: canvasState,
      roundKey,
    });
  }
  const planPayload = { ...resolveObject(streaming.planPayload), ...resolveObject(streaming.canonicalPayload) };
  // Only this Run's accepted stream projection establishes delivery, not retained session context.
  items.push(...buildCommittedArtifactItems({ deliveredArtifacts: Object.hasOwn(streaming, 'deliveredArtifacts')
    ? streaming.deliveredArtifacts : streaming.planPayload?.delivered_artifacts,
  conversationProjection: streaming.knowledgeCitation?.conversation_projection, roundKey }));
  if (Object.keys(planPayload).length) {
    items.push({
      id: 'primary-flow-payload-latest',
      type: PRIMARY_FLOW_PAYLOAD_TIMELINE_TYPE,
      payload: planPayload,
      roundKey,
    });
    const artifactItem = buildMessagePostGenerationArtifactItem({
      postGenerationResult: planPayload.post_generation_result,
      id: 'latest',
      roundKey,
      source: 'streaming',
    });
    if (artifactItem) {
      items.push(artifactItem);
    }
  }
  if (resolveObject(streaming.workflowProjection)) {
    items.push({
      id: 'workflow-projection-latest',
      type: 'workflow_projection',
      payload: streaming.workflowProjection,
      roundKey,
    });
  }
  if (resolveObject(streaming.analysisPayload)) {
    items.push({
      id: 'analysis-payload-latest',
      type: 'analysis_payload',
      payload: streaming.analysisPayload,
      roundKey,
    });
  }
  if (resolveObject(streaming.knowledgeSearch)) {
    items.push({
      id: `knowledge-search-${String(firstTruthy(() => (streaming.knowledgeSearch.run_id), () => ('latest')))}`,
      type: 'knowledge_search',
      payload: streaming.knowledgeSearch,
      roundKey,
    });
  }
  if (resolveObject(streaming.sourcingCandidates)) {
    const analysisRoute = firstTruthy(() => (resolveObject(streaming.sourcingCandidates.analysis_route)), () => (resolveObject(streaming.analysisRoute)));
    items.push({
      id: `sourcing-candidates-${String(firstTruthy(() => (streaming.sourcingCandidates.run_id), () => ('latest')))}`,
      type: 'sourcing_candidates',
      payload: streaming.sourcingCandidates,
      analysisRoute,
      roundKey,
    });
  }
}

function appendStreamingContentItem(items, streaming, loading = false) {
  const presentation = selectMessagePresentation({ role: 'assistant', content: streaming.content,
    knowledge_citation: streaming.knowledgeCitation });
  if (!presentation.visible) return;
  const mergedStreamingContent = presentation.content;
  if (!mergedStreamingContent && !loading) {
    return;
  }
  items.push({
    id: 'streaming-message',
    type: 'streaming',
    content: mergedStreamingContent,
    citationPayload: presentation.citationPayload,
    generating: Boolean(streaming.generating),
    runState: streaming.runState ?? '',
    messageIdentityKey: streaming.messageIdentityKey ?? messageIdentityKeyForExecutionProfile(streaming.executionProfile),
    pending: !mergedStreamingContent,
    roundKey: String(streaming.roundKey || '').trim(),
  });
  if (presentation.remainingPayload) items.push({ id: `knowledge-citation-${streaming.roundKey || 'streaming'}`,
    type: 'knowledge_citation', payload: presentation.remainingPayload, roundKey: String(streaming.roundKey || '').trim() });
}

function hasWorkflowProgress(workflowProjection) {
  return Boolean(resolveObject(workflowProjection?.progress_projection))
    || (Array.isArray(workflowProjection?.run_steps) && workflowProjection.run_steps.length > 0);
}

function appendThinkingItem(items, {
  streaming,
  executionCards,
  loading,
}) {
  /**
   * DOC HELPER — OBJ-09 frontend SSOT boundary.
   *
   * The timeline may display backend status, but must not convert completed
   * states into waiting_user by inspecting primary-flow payloads. Waiting states must
   * come from backend agent/execution status.
   */
  const resolvedAgentStatus = resolveObject(streaming.agentStatus);
  const resolvedPhase = resolveObject(streaming.phase);
  const workflowProjection = resolveObject(streaming.workflowProjection);
  const planPayload = resolveObject(streaming.planPayload);
  const workflowTracker = resolveObject(planPayload?.workflow_tracker);
  const trackResult = resolveObject(planPayload?.track_result);
  const hasBackendWorkflowProgress = hasWorkflowProgress(workflowProjection);
  const hasThinkingSignal = ['connecting', 'streaming'].includes(streaming.status) || Boolean(
    firstTruthy(() => (streaming.runState), () => (resolvedAgentStatus), () => (resolvedPhase), () => (hasBackendWorkflowProgress), () => (workflowTracker), () => (trackResult), () => (streaming.thinkingTrace), () => (streaming.executionTrace), () => (executionCards.length > 0))
  );
  if (!hasThinkingSignal) return false;
  items.push({
    id: `process:${String(streaming.roundKey || 'pending')}`,
    processIdentity: processIdentity(streaming.sessionId, streaming.runId, streaming.backendRequestId),
    projectionSequence: processProjectionSequence(workflowProjection),
    processPhases: streaming.processPhases ?? [],
    type: 'thinking',
    messageIdentityKey: streaming.messageIdentityKey ?? messageIdentityKeyForExecutionProfile(streaming.executionProfile),
    agentStatus: resolvedAgentStatus,
    phase: resolvedPhase,
    thinkingTrace: streaming.thinkingTrace,
    executionTrace: streaming.executionTrace,
    executionCards,
    workflowProjection,
    workflowTracker,
    trackResult,
    roundKey: String(firstTruthy(() => (streaming.roundKey), () => (''))).trim(),
    loading,
  });
  return true;
}

export function appendStreamingTimelineItems(items, {
  streaming,
  executionCards,
  loading,
}) {
  appendStreamingArtifactItems(items, streaming);
  const hasThinkingItem = appendThinkingItem(items, {
    streaming,
    executionCards,
    loading,
  });
  appendStreamingContentItem(items, streaming, Boolean(loading && !hasThinkingItem));
}
